// Page bookkeeping for documents read from v2 procedures/image-result (owner 10-07, "reports
// over 10 pages - I can only see 5"). image-result returns a window of pages plus total_pages;
// the rest is asked for with page_offset = the number of pages held WITHOUT A GAP (pages 1..k).
// A page that failed to load in an earlier window (FHIR Binary error) leaves a gap: asking from
// pages.length instead skipped that page for good and left a "Load remaining" button that could
// never finish (review 10-07 F4). All of it is free: v2 serves pages from its disk cache or FHIR.

// 1-based page number of a page dict, or null when its pageid is not a positive integer.
export const pageNumber = (page) => {
    const n = Number(page?.pageid);
    return Number.isInteger(n) && n > 0 ? n : null;
};

// `prev` plus the pages of `fetched` it does not hold yet (by pageid), in page order when every
// page is numbered. Returns `prev` itself when nothing is new.
export const mergePages = (prev, fetched) => {
    const held = prev || [];
    const seen = new Set(held.map((p, i) => String(p?.pageid ?? `#${i}`)));
    const fresh = [];
    for (const p of fetched || []) {
        const key = String(p?.pageid);
        if (!seen.has(key)) {
            seen.add(key);
            fresh.push(p);
        }
    }
    if (!fresh.length) return prev;
    const all = [...held, ...fresh];
    return all.every((p) => pageNumber(p) !== null)
        ? all.sort((a, b) => pageNumber(a) - pageNumber(b))
        : all;
};

// Where the next request starts: k when pages 1..k are all held (pages without numbers: their count).
export const contiguousCount = (pages) => {
    const list = pages || [];
    const have = new Set(list.map(pageNumber).filter((n) => n !== null));
    if (!have.size) return list.length;
    let k = 0;
    while (have.has(k + 1)) k += 1;
    return k;
};

// The pages after a first image-result response, until the document is complete, a batch brings
// nothing new, a request fails, or `isCancelled()` says the caller is gone. Returns every page
// held (the first ones included), in page order.
export const fetchRemainingPages = async (post, url, body, firstPages, firstTotal,
    { batch = 50, maxRounds = 20, isCancelled = () => false } = {}) => {
    let pages = firstPages || [];
    let total = Number(firstTotal) || 0;
    for (let round = 0; round < maxRounds && pages.length < total; round += 1) {
        if (isCancelled()) break;
        let res;
        try {
            res = await post(url, { ...body, page_offset: contiguousCount(pages), page_limit: batch });
        } catch (e) {
            break;
        }
        const data = res?.data || res;
        const entry = Array.isArray(data) ? data[0] : data;
        const next = mergePages(pages, entry?.pages || []);
        total = Math.max(total, Number(entry?.total_pages) || 0);
        if (next === pages) break;
        pages = next;
    }
    return pages;
};
